import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { analytics, capPeriodId } from '@/lib/analytics';

interface UsageBannerProps {
  used: number;
  limit: number;
  isDesktop?: boolean;
}

// Mirrors the width math from the webpage card in src/pages/Saved.tsx so the
// banner sits in the list as a visual peer (same column width per breakpoint).
const DESKTOP_WIDTH_CLASSES =
  'md:w-[calc((768px-2rem)/2-24px-1px)] lg:w-[calc((1024px-2rem-32px-16px-32px)/2-1px)] xl:w-[calc((1280px-2rem-32px-24px-32px)/2-1px)] 2xl:w-[calc((1600px-2rem-40px-128px-32px)/2-1px)]';

export function UsageBanner({ used, limit, isDesktop = false }: UsageBannerProps) {
  const atCap = used >= limit;
  const remaining = Math.max(limit - used, 0);
  const pct = Math.min(100, (used / limit) * 100);

  // Banner-view + cap-state lifecycle. Track once per mount to avoid spam from
  // the parent re-rendering on every save list update. atCap distinguishes
  // the red ≥100% surface from the amber ≥80% one — drives the trigger_source
  // split (banner_red vs banner_amber) in the upgrade_intent funnel.
  const firedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (firedRef.current.has('view')) return;
    firedRef.current.add('view');
    const periodId = capPeriodId();
    analytics.track('usage_banner_viewed', {
      cap_type: 'saves',
      used, limit,
      cap_pct: Math.round((used / limit) * 100),
      severity: atCap ? 'red' : 'amber',
      cap_period_id: periodId,
    });
    if (!atCap && pct >= 80 && !firedRef.current.has('approaching')) {
      firedRef.current.add('approaching');
      analytics.track('cap_approaching_80', { cap_type: 'saves', used, limit, cap_period_id: periodId });
    }
  }, [atCap, used, limit, pct]);

  const headline = atCap
    ? 'Saver cap reached. Delete some saves or upgrade to Pro for unlimited saves.'
    : `${remaining} save${remaining === 1 ? '' : 's'} left. Upgrade to Pro for unlimited saves.`;

  return (
    <div
      className={`w-full ${isDesktop ? DESKTOP_WIDTH_CLASSES : ''} rounded border border-neutral-200 p-4 ${
        atCap
          ? 'bg-gradient-to-br from-red-50/50 via-white to-white'
          : 'bg-gradient-to-br from-amber-50/50 via-white to-white'
      }`}
    >
      {/* Title row — mirrors favicon+title row on webpage cards */}
      <div className="flex items-center gap-2 w-full">
        <h3 className="font-semibold text-neutral-800 truncate flex-1">Saver plan</h3>
        <p className="text-xs text-neutral-400 tabular-nums shrink-0">
          <span className={`font-medium ${atCap ? 'text-red-600' : 'text-neutral-800'}`}>
            {used}
          </span>
          <span className="mx-0.5 text-neutral-300">/</span>
          {limit}
        </p>
      </div>

      {/* Body slot — meter + headline, mirrors description min-h-[2.5rem] */}
      <div className="mt-2 min-h-[2.5rem]">
        <div className="h-1.5 w-full rounded-full bg-neutral-100 overflow-hidden">
          <div
            className={`h-full transition-all ${atCap ? 'bg-red-500' : 'bg-amber-500'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="text-sm text-neutral-500 mt-2 line-clamp-2 leading-snug">
          {headline}
        </p>
      </div>

      {/* Footer row — mirrors URL+date row on webpage cards */}
      <div className="mt-2 flex items-center justify-between gap-2">
        <UpgradeLink atCap={atCap} used={used} limit={limit} />
      </div>
    </div>
  );
}

function UpgradeLink({ atCap, used, limit }: { atCap: boolean; used: number; limit: number }) {
  const navigate = useNavigate();
  const trigger = atCap ? 'banner_red' : 'banner_amber';
  return (
    <button
      type="button"
      onClick={() => {
        const id = analytics.beginUpgradeIntent(trigger, {
          cap_type: 'saves',
          used,
          limit,
          cap_pct: Math.round((used / limit) * 100),
          destination_path: '/upgrade',
        });
        navigate(`/upgrade?upgrade_intent_id=${id}&from=${trigger}`);
      }}
      className="text-sm text-primary hover:underline truncate min-w-0 text-left bg-transparent border-0 p-0"
    >
      Upgrade to Pro →
    </button>
  );
}
